import { useState } from "react"
import { useQuery, useMutation } from "@tanstack/react-query"
import { useLocation } from "wouter"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Switch } from "@/components/ui/switch"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { 
  Users, Building2, Zap, TrendingUp, UserCheck, UserX, 
  Calendar, MapPin, Phone, Mail, Linkedin, Globe,
  Activity, Clock, CheckCircle, XCircle, Trash2, 
  Crown, Shield, CreditCard, Ban, HourglassIcon, Check, X, Sparkles,
  ArrowLeft, Home, Archive, RotateCcw, Eye, MousePointer, PlayCircle, ExternalLink
} from "lucide-react"
import { AnimatedSection } from "@/components/AnimatedSection"
import { SignalRoomAdminPanel } from "@/components/SignalRoomAdminPanel"
import { FloatingCard } from "@/components/FloatingCard"
import { useToast } from "@/hooks/use-toast"
import { apiRequest, queryClient } from "@/lib/queryClient"
import type { User, Company, Automation } from "@shared/schema"

// GET /api/admin/companies annotates each company with a computed `hasStrategy` flag
// (whether a marketing_strategies row exists) — not a DB column on Company itself.
type CompanyWithStrategy = Company & { hasStrategy?: boolean }

interface AdminStats {
  users: {
    total: number;
    onboarded: number;
    admins: number;
    recentSignups: number;
  };
  companies: {
    total: number;
    withTargetAudience: number;
    withValueProposition: number;
    industries: number;
  };
  automations: {
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
    failed: number;
  };
}

interface PreviewStat {
  slug: string;
  companyName: string;
  views: number;
  videoClicks: number;
  ctaClicks: number;
  lastViewedAt: string | null;
  status: string;
  createdAt: string;
}

interface PreviewEvent {
  id: number;
  eventType: string;
  createdAt: string;
}

interface VslStats {
  totalViews: number;
  totalVideoPlays: number;
  fromPreviews: number;
  directVisits: number;
  conversionRate: number;
}

function StatCard({ title, value, description, icon: Icon, trend }: {
  title: string;
  value: number;
  description: string;
  icon: any;
  trend?: string;
}) {
  return (
    <FloatingCard>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="h-5 w-5 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground flex items-center mt-1">
          {description}
          {trend && (
            <Badge variant="outline" className="ml-2 text-xs">
              {trend}
            </Badge>
          )}
        </p>
      </CardContent>
    </FloatingCard>
  )
}


function CompanyRow({ company }: { company: CompanyWithStrategy }) {
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const deleteCompanyMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest('DELETE', `/api/admin/companies/${company.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/companies'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/stats'] });
      toast({
        title: "Unternehmen gelöscht",
        description: `"${company.name}" wurde erfolgreich gelöscht.`
      });
      setShowDeleteDialog(false);
    },
    onError: () => {
      toast({
        title: "Fehler",
        description: "Unternehmen konnte nicht gelöscht werden.",
        variant: "destructive"
      });
    }
  });

  const generateStrategyMutation = useMutation({
    mutationFn: async (force?: boolean) => {
      return await apiRequest('POST', `/api/admin/companies/${company.id}/generate-strategy`, force ? { force: true } : {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/companies'] });
      toast({
        title: "Strategie-Generierung gestartet",
        description: `Die Strategie für "${company.name}" wird jetzt erstellt.`
      });
    },
    onError: (error: any) => {
      toast({
        title: "Fehler",
        description: error?.message || "Strategie-Generierung konnte nicht gestartet werden.",
        variant: "destructive"
      });
    }
  });

  return (
    <div className="p-4 border-b border-border last:border-b-0" data-testid={`company-row-${company.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <h3 className="text-sm font-medium mb-1" data-testid="company-name">
            {company.name}
          </h3>
          <Badge variant="outline" className="text-xs mb-2">
            {company.industry}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Strategy generation for companies that don't have one yet */}
          {company.hasStrategy === false && (
            <div className="flex items-center gap-2 mr-2">
              <Badge variant="outline" className="text-xs">
                Keine Strategie
              </Badge>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => generateStrategyMutation.mutate(undefined)}
                disabled={generateStrategyMutation.isPending}
                data-testid={`button-generate-strategy-${company.id}`}
              >
                {generateStrategyMutation.isPending ? "Wird gestartet..." : "Strategie generieren"}
              </Button>
            </div>
          )}
          {company.targetAudience && (
            <Badge variant="secondary" className="text-xs">
              Zielgruppe
            </Badge>
          )}
          {company.valueProposition && (
            <Badge variant="outline" className="text-xs">
              Wertversprechen
            </Badge>
          )}
          {company.contentDnaCompleted && (
            <Badge variant="default" className="text-xs">
              A-Team Briefing
            </Badge>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-xs text-muted-foreground">
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Users className="h-3 w-3" />
            <span>{company.contactFirstName} {company.contactLastName}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Mail className="h-3 w-3" />
            <span data-testid="company-email">{company.contactEmail}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Phone className="h-3 w-3" />
            <span>{company.contactPhone || 'Nicht angegeben'}</span>
          </div>
        </div>
        
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Globe className="h-3 w-3" />
            <span className="truncate">{company.website}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Linkedin className="h-3 w-3" />
            <span className="truncate">LinkedIn-Profil</span>
          </div>
          <div className="flex items-center space-x-2">
            <MapPin className="h-3 w-3" />
            <span>{company.address || 'Nicht angegeben'}</span>
          </div>
        </div>
      </div>

      {company.brandVoiceSummary && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-start space-x-2 text-xs">
            <Sparkles className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium text-foreground">Brand Voice Summary (AI):</span>
              <p className="text-muted-foreground mt-1" data-testid="brand-voice-summary">
                {company.brandVoiceSummary}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Button */}
      <div className="mt-3 pt-3 border-t border-border flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowDeleteDialog(true)}
          className="text-destructive"
        >
          <Trash2 className="h-3 w-3 mr-1" />
          Löschen
        </Button>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unternehmen löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchtest du "{company.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteCompanyMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteCompanyMutation.isPending}
            >
              {deleteCompanyMutation.isPending ? "Wird gelöscht..." : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function AutomationRow({ automation }: { automation: Automation }) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-primary" />
      case 'failed': return <XCircle className="h-4 w-4 text-destructive" />
      case 'in_progress': return <Activity className="h-4 w-4 text-primary animate-pulse" />
      default: return <Clock className="h-4 w-4 text-muted-foreground" />
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed': return <Badge variant="secondary" className="text-xs">Abgeschlossen</Badge>
      case 'failed': return <Badge variant="destructive" className="text-xs">Fehlgeschlagen</Badge>
      case 'in_progress': return <Badge variant="outline" className="text-xs">In Bearbeitung</Badge>
      default: return <Badge variant="outline" className="text-xs">Ausstehend</Badge>
    }
  }

  return (
    <div className="flex flex-wrap items-center space-x-4 p-4 border-b border-border last:border-b-0" data-testid={`automation-row-${automation.id}`}>
      {getStatusIcon(automation.status || 'pending')}
      <div className="flex-1">
        <div className="flex flex-wrap items-center space-x-2 mb-1">
          <h3 className="text-sm font-medium" data-testid="automation-phase">
            {automation.phase}
          </h3>
          {getStatusBadge(automation.status || 'pending')}
        </div>
        <p className="text-xs text-muted-foreground">
          Erstellt: {new Date(automation.createdAt!).toLocaleDateString('de-DE')}
        </p>
      </div>
    </div>
  )
}

export default function AdminDashboard() {
  const [, setLocation] = useLocation()
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set())
  const [rejectingUser, setRejectingUser] = useState<User | null>(null)
  const { toast } = useToast()

  const { data: stats, isLoading: statsLoading } = useQuery<AdminStats>({
    queryKey: ['/api/admin/stats']
  })

  const { data: users = [], isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ['/api/admin/users']
  })

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await apiRequest('DELETE', `/api/admin/users/${userId}`)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] })
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] })
      toast({
        title: "Benutzer gelöscht",
        description: "Der Benutzer wurde erfolgreich entfernt.",
      })
    },
    onError: (error: any) => {
      toast({
        title: "Fehler beim Löschen",
        description: error.message || "Der Benutzer konnte nicht gelöscht werden.",
        variant: "destructive",
      })
    }
  })

  // Reset onboarding mutation
  const resetOnboardingMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await apiRequest('POST', `/api/admin/users/${userId}/reset-onboarding`)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] })
      toast({
        title: "Onboarding zurückgesetzt",
        description: "Der Benutzer kann jetzt erneut das Onboarding durchlaufen.",
      })
    },
    onError: (error: any) => {
      toast({
        title: "Fehler beim Zurücksetzen",
        description: error.message || "Das Onboarding konnte nicht zurückgesetzt werden.",
        variant: "destructive",
      })
    }
  })

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, updates }: { userId: string, updates: any }) => {
      return await apiRequest('PATCH', `/api/admin/users/${userId}`, updates)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] })
      toast({
        title: "Benutzer aktualisiert",
        description: "Die Änderungen wurden erfolgreich gespeichert.",
      })
    },
    onError: (error: any) => {
      toast({
        title: "Fehler beim Aktualisieren",
        description: error.message || "Die Änderungen konnten nicht gespeichert werden.",
        variant: "destructive",
      })
    }
  })

  const { data: companies = [], isLoading: companiesLoading } = useQuery<CompanyWithStrategy[]>({
    queryKey: ['/api/admin/companies']
  })

  // Access toggle now notifies n8n server-side (see PATCH /api/admin/users/:id); the browser no
  // longer holds the webhook URL/secret.

  const { data: automations = [], isLoading: automationsLoading } = useQuery<Automation[]>({
    queryKey: ['/api/admin/automations']
  })

  // Query for archived (soft-deleted) users
  const { data: archivedUsers = [], isLoading: archivedUsersLoading } = useQuery<User[]>({
    queryKey: ['/api/admin/users/archived']
  })

  // Preview page stats
  const [previewDays, setPreviewDays] = useState(30)
  const [selectedPreviewSlug, setSelectedPreviewSlug] = useState<string | null>(null)
  
  const { data: previewStats = [], isLoading: previewStatsLoading } = useQuery<PreviewStat[]>({
    queryKey: ['/api/admin/preview/stats', previewDays],
    queryFn: async () => {
      const response = await fetch(`/api/admin/preview/stats?days=${previewDays}`)
      const result = await response.json()
      return result.stats || []
    }
  })

  const { data: previewEvents = [], isLoading: previewEventsLoading } = useQuery<PreviewEvent[]>({
    queryKey: ['/api/admin/preview', selectedPreviewSlug, 'events', previewDays],
    queryFn: async () => {
      if (!selectedPreviewSlug) return []
      const response = await fetch(`/api/admin/preview/${selectedPreviewSlug}/events?days=${previewDays}`)
      const result = await response.json()
      return result.events || []
    },
    enabled: !!selectedPreviewSlug
  })

  const { data: vslStats } = useQuery<VslStats>({
    queryKey: ['/api/admin/vsl/stats', previewDays],
    queryFn: async () => {
      const response = await fetch(`/api/admin/vsl/stats?days=${previewDays}`)
      const result = await response.json()
      return result.stats || { totalViews: 0, totalVideoPlays: 0, fromPreviews: 0, directVisits: 0, conversionRate: 0 }
    }
  })

  // Restore user mutation
  const restoreUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await apiRequest('PATCH', `/api/admin/users/${userId}/restore`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] })
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users/archived"] })
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] })
      toast({
        title: "Benutzer wiederhergestellt",
        description: "Der Benutzer wurde erfolgreich wiederhergestellt.",
      })
    },
    onError: (error: any) => {
      toast({
        title: "Fehler beim Wiederherstellen",
        description: error.message || "Der Benutzer konnte nicht wiederhergestellt werden.",
        variant: "destructive",
      })
    }
  })

  // Permanent delete mutation
  const permanentDeleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await apiRequest('DELETE', `/api/admin/users/${userId}/permanent`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users/archived"] })
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] })
      toast({
        title: "Benutzer endgültig gelöscht",
        description: "Der Benutzer wurde dauerhaft aus dem System entfernt.",
      })
    },
    onError: (error: any) => {
      toast({
        title: "Fehler beim Löschen",
        description: error.message || "Der Benutzer konnte nicht gelöscht werden.",
        variant: "destructive",
      })
    }
  })

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground">Lade Admin-Dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8 pb-8">
      {/* Navigation Header */}
      <div className="flex items-center gap-3 pb-2 border-b">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => setLocation('/')}
          data-testid="button-back-dashboard"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zum Dashboard
        </Button>
      </div>

      {/* Header */}
      <AnimatedSection delay={100}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="admin-dashboard-title">
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground">
              Umfassende Übersicht über alle Kunden und Systemaktivitäten
            </p>
          </div>
          <Button variant="outline" data-testid="button-refresh">
            <TrendingUp className="h-4 w-4 mr-2" />
            Aktualisieren
          </Button>
        </div>
      </AnimatedSection>

      {/* Statistics Overview */}
      {stats && (
        <AnimatedSection delay={200}>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <StatCard
              title="Gesamte Benutzer"
              value={stats.users.total}
              description={`${stats.users.onboarded} vollständig ongeboardet`}
              icon={Users}
              trend={`${stats.users.recentSignups} diese Woche`}
            />
            <StatCard
              title="Unternehmen"
              value={stats.companies.total}
              description={`${stats.companies.industries} verschiedene Branchen`}
              icon={Building2}
            />
            <StatCard
              title="Aktive Automatisierungen"
              value={stats.automations.inProgress}
              description={`${stats.automations.completed} abgeschlossen`}
              icon={Zap}
            />
            <StatCard
              title="KI-Ready Unternehmen"
              value={stats.companies.withTargetAudience}
              description="Mit Zielgruppe & Wertversprechen"
              icon={TrendingUp}
            />
          </div>
        </AnimatedSection>
      )}

      {/* Main Content Tabs */}
      <AnimatedSection delay={300}>
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="users" data-testid="tab-users">
              Benutzer ({stats?.users.total || 0})
            </TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived">
              <Archive className="h-4 w-4 mr-1" />
              Archiviert ({archivedUsers.length})
            </TabsTrigger>
            <TabsTrigger value="companies" data-testid="tab-companies">
              Unternehmen ({stats?.companies.total || 0})
            </TabsTrigger>
            <TabsTrigger value="automations" data-testid="tab-automations">
              Automatisierungen ({stats?.automations.total || 0})
            </TabsTrigger>
            <TabsTrigger value="previews" data-testid="tab-previews">
              <Eye className="h-4 w-4 mr-1" />
              Previews ({previewStats.length})
            </TabsTrigger>
            <TabsTrigger value="signal-room" data-testid="tab-signal-room">
              <Activity className="h-4 w-4 mr-1" />
              Signal Room
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <FloatingCard>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5" />
                  <span>Benutzer-Management</span>
                </CardTitle>
                <CardDescription>
                  Übersicht aller registrierten Benutzer und deren Onboarding-Status
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {usersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center space-y-4">
                      <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                      <p className="text-sm text-muted-foreground">Lade Benutzer...</p>
                    </div>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">Keine Benutzer gefunden</h3>
                    <p className="text-muted-foreground">
                      Es sind noch keine Benutzer registriert.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border" data-testid="users-list">
                    {users.map((user) => {
                      const isExpanded = expandedUsers.has(user.id)
                      const getSubscriptionBadge = () => {
                        if (user.accessEnabled === false) {
                          return <Badge variant="destructive" className="text-xs"><Ban className="h-3 w-3 mr-1" />Gesperrt</Badge>
                        }
                        switch (user.subscriptionStatus) {
                          case 'active': return <Badge variant="default" className="text-xs"><CreditCard className="h-3 w-3 mr-1" />Aktiv</Badge>
                          case 'trial': return <Badge variant="secondary" className="text-xs"><Clock className="h-3 w-3 mr-1" />Testphase</Badge>
                          case 'expired': return <Badge variant="destructive" className="text-xs"><XCircle className="h-3 w-3 mr-1" />Abgelaufen</Badge>
                          case 'cancelled': return <Badge variant="outline" className="text-xs"><Ban className="h-3 w-3 mr-1" />Gekündigt</Badge>
                          default: return <Badge variant="secondary" className="text-xs">Unbekannt</Badge>
                        }
                      }
                      
                      const getWaitlistBadge = () => {
                        switch (user.status) {
                          case 'pending': return <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-700 dark:text-yellow-400"><HourglassIcon className="h-3 w-3 mr-1" />Warteliste</Badge>
                          case 'approved': return <Badge variant="default" className="text-xs"><CheckCircle className="h-3 w-3 mr-1" />Freigegeben</Badge>
                          case 'rejected': return <Badge variant="destructive" className="text-xs"><XCircle className="h-3 w-3 mr-1" />Abgelehnt</Badge>
                          default: return <Badge variant="secondary" className="text-xs">Unbekannt</Badge>
                        }
                      }
                      
                      return (
                        <div key={user.id}>
                          {/* Main User Row */}
                          <div className="flex flex-wrap items-center gap-3 p-4 border-b border-border" data-testid={`row-member-${user.id}`}>
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="text-xs">
                                {user.firstName?.[0]}{user.lastName?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="text-sm font-medium" data-testid="user-name">
                                  {user.firstName} {user.lastName}
                                </span>
                                <Badge variant={user.role === 'admin' ? 'default' : 'secondary'} className="text-xs">
                                  {user.role === 'admin' ? <Crown className="h-3 w-3 mr-1" /> : <Shield className="h-3 w-3 mr-1" />}
                                  {user.role}
                                </Badge>
                                {getWaitlistBadge()}
                                {getSubscriptionBadge()}
                                <Badge variant={user.subscriptionTier === 'premium' ? 'default' : 'outline'} className="text-xs">
                                  {user.subscriptionTier}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground truncate" data-testid="user-email">
                                {user.email}
                              </p>
                            </div>

                            {/* User Management Controls */}
                            <div className="flex flex-wrap items-center gap-2">
                              {/* Access Toggle */}
                              <div className="flex items-center space-x-2">
                                <Switch
                                  checked={user.accessEnabled ?? false}
                                  onCheckedChange={(checked) => {
                                    // Update user access in DB; the server mirrors the change to n8n.
                                    updateUserMutation.mutate({
                                      userId: user.id,
                                      updates: { accessEnabled: checked }
                                    })
                                  }}
                                  disabled={updateUserMutation.isPending}
                                  data-testid={`switch-access-${user.id}`}
                                />
                                <span className="text-xs text-muted-foreground">
                                  {user.accessEnabled ? 'Aktiv' : 'Gesperrt'}
                                </span>
                              </div>
                              
                              {/* Waitlist Action Buttons */}
                              <div className="flex items-center gap-1">
                                {user.status !== 'approved' && (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => {
                                      updateUserMutation.mutate({
                                        userId: user.id,
                                        updates: { status: 'approved' }
                                      })
                                    }}
                                    disabled={updateUserMutation.isPending}
                                    data-testid={`button-approve-${user.id}`}
                                  >
                                    <Check className="h-4 w-4 mr-1" />
                                    Freigeben
                                  </Button>
                                )}
                                {user.status !== 'rejected' && (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => setRejectingUser(user)}
                                    disabled={updateUserMutation.isPending}
                                    data-testid={`button-reject-${user.id}`}
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Ablehnen
                                  </Button>
                                )}
                              </div>
                              
                              {/* Details Button */}
                              <Button 
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const newExpanded = new Set(expandedUsers)
                                  if (isExpanded) {
                                    newExpanded.delete(user.id)
                                  } else {
                                    newExpanded.add(user.id)
                                  }
                                  setExpandedUsers(newExpanded)
                                }}
                                data-testid={`button-view-member-${user.id}`}
                              >
                                {isExpanded ? 'Weniger' : 'Details'}
                              </Button>

                              {/* Reset Onboarding Button */}
                              <Button 
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (window.confirm(`Onboarding für ${user.firstName} ${user.lastName} zurücksetzen? Der Benutzer muss dann erneut das Onboarding durchlaufen.`)) {
                                    resetOnboardingMutation.mutate(user.id)
                                  }
                                }}
                                disabled={resetOnboardingMutation.isPending}
                                title="Onboarding zurücksetzen"
                                data-testid={`button-reset-onboarding-${user.id}`}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>

                              {/* Delete Button */}
                              <Button 
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  if (window.confirm(`Möchten Sie ${user.firstName} ${user.lastName} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
                                    deleteUserMutation.mutate(user.id)
                                  }
                                }}
                                disabled={deleteUserMutation.isPending}
                                data-testid={`button-delete-${user.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Details Section */}
                          {isExpanded && (
                            <div className="bg-muted/30 p-4 border-b" data-testid={`user-details-${user.id}`}>
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                  <h4 className="font-semibold mb-3 text-sm">Benutzer-Details</h4>
                                  <div className="space-y-2 text-xs">
                                    <p><strong>ID:</strong> {user.id}</p>
                                    <p><strong>E-Mail:</strong> {user.email}</p>
                                    <p><strong>Rolle:</strong> {user.role}</p>
                                    <p><strong>Onboarding:</strong> {user.isOnboarded ? 'Abgeschlossen' : 'Ausstehend'}</p>
                                    <p><strong>Registriert:</strong> {user.createdAt ? new Date(user.createdAt).toLocaleDateString('de-DE') : 'Unbekannt'}</p>
                                  </div>
                                </div>
                                
                                <div>
                                  <h4 className="font-semibold mb-3 text-sm">Abo & Zahlungen</h4>
                                  <div className="space-y-2 text-xs">
                                    <p><strong>Status:</strong> {user.subscriptionStatus || 'Unbekannt'}</p>
                                    <p><strong>Tarif:</strong> {user.subscriptionTier || 'Basic'}</p>
                                    <p><strong>Zugang:</strong> {user.accessEnabled ? 'Aktiv' : 'Gesperrt'}</p>
                                    <p><strong>Läuft ab:</strong> {user.subscriptionExpiresAt ? new Date(user.subscriptionExpiresAt).toLocaleDateString('de-DE') : 'Unbegrenzt'}</p>
                                    <p><strong>Letzte Zahlung:</strong> {user.lastPaymentDate ? new Date(user.lastPaymentDate).toLocaleDateString('de-DE') : 'Keine'}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </FloatingCard>
          </TabsContent>

          <TabsContent value="archived">
            <FloatingCard>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Archive className="h-5 w-5" />
                  <span>Archivierte Benutzer</span>
                </CardTitle>
                <CardDescription>
                  Gelöschte Benutzer, die nach 30 Tagen endgültig entfernt werden
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {archivedUsersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center space-y-4">
                      <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                      <p className="text-sm text-muted-foreground">Lade archivierte Benutzer...</p>
                    </div>
                  </div>
                ) : archivedUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <Archive className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">Keine archivierten Benutzer</h3>
                    <p className="text-muted-foreground">
                      Es wurden noch keine Benutzer gelöscht.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border" data-testid="archived-users-list">
                    {archivedUsers.map((user) => {
                      const deletedDate = user.deletedAt ? new Date(user.deletedAt) : null;
                      const daysUntilPermanentDelete = deletedDate 
                        ? Math.max(0, 30 - Math.floor((Date.now() - deletedDate.getTime()) / (1000 * 60 * 60 * 24)))
                        : 0;
                      
                      return (
                        <div key={user.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`row-archived-${user.id}`}>
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs">
                              {user.firstName?.[0]}{user.lastName?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="text-sm font-medium" data-testid="archived-user-name">
                                {user.firstName} {user.lastName}
                              </span>
                              <Badge variant="destructive" className="text-xs">
                                <Trash2 className="h-3 w-3 mr-1" />
                                Gelöscht
                              </Badge>
                              {daysUntilPermanentDelete <= 7 && (
                                <Badge variant="outline" className="text-xs border-orange-500 text-orange-600">
                                  {daysUntilPermanentDelete} Tage übrig
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate" data-testid="archived-user-email">
                              {user.email}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Gelöscht am: {deletedDate?.toLocaleDateString('de-DE')} • Endgültig in {daysUntilPermanentDelete} Tagen
                            </p>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => restoreUserMutation.mutate(user.id)}
                              disabled={restoreUserMutation.isPending}
                              data-testid={`button-restore-${user.id}`}
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Wiederherstellen
                            </Button>
                            
                            <Button 
                              variant="destructive"
                              size="sm"
                              onClick={() => {
                                if (window.confirm(`Möchten Sie ${user.firstName} ${user.lastName} ENDGÜLTIG löschen? Diese Aktion kann NICHT rückgängig gemacht werden!`)) {
                                  permanentDeleteMutation.mutate(user.id)
                                }
                              }}
                              disabled={permanentDeleteMutation.isPending}
                              data-testid={`button-permanent-delete-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Endgültig löschen
                            </Button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </FloatingCard>
          </TabsContent>

          <TabsContent value="companies">
            <FloatingCard>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Building2 className="h-5 w-5" />
                  <span>Unternehmen-Management</span>
                </CardTitle>
                <CardDescription>
                  Detaillierte Übersicht aller Kundenunternehmen und deren Profile
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {companiesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center space-y-4">
                      <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                      <p className="text-sm text-muted-foreground">Lade Unternehmen...</p>
                    </div>
                  </div>
                ) : companies.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">Keine Unternehmen gefunden</h3>
                    <p className="text-muted-foreground">
                      Es sind noch keine Unternehmen registriert.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border" data-testid="companies-list">
                    {companies.map(company => (
                      <CompanyRow key={company.id} company={company} />
                    ))}
                  </div>
                )}
              </CardContent>
            </FloatingCard>
          </TabsContent>

          <TabsContent value="automations">
            <FloatingCard>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Zap className="h-5 w-5" />
                  <span>Automatisierungs-Monitoring</span>
                </CardTitle>
                <CardDescription>
                  Status-Übersicht aller laufenden und abgeschlossenen Automatisierungen
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {automationsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center space-y-4">
                      <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                      <p className="text-sm text-muted-foreground">Lade Automatisierungen...</p>
                    </div>
                  </div>
                ) : automations.length === 0 ? (
                  <div className="text-center py-12">
                    <Zap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">Keine Automatisierungen gefunden</h3>
                    <p className="text-muted-foreground">
                      Es sind noch keine Automatisierungen gestartet.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border" data-testid="automations-list">
                    {automations.map(automation => (
                      <AutomationRow key={automation.id} automation={automation} />
                    ))}
                  </div>
                )}
              </CardContent>
            </FloatingCard>
          </TabsContent>

          <TabsContent value="previews">
            <FloatingCard>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Eye className="h-5 w-5" />
                  <span>Preview-Seiten Tracking</span>
                </CardTitle>
                <CardDescription className="flex items-center justify-between">
                  <span>Übersicht aller personalisierten Vorschau-Seiten für Cold Outreach</span>
                  <div className="flex gap-2">
                    <Button 
                      variant={previewDays === 7 ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setPreviewDays(7)}
                    >
                      7 Tage
                    </Button>
                    <Button 
                      variant={previewDays === 30 ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setPreviewDays(30)}
                    >
                      30 Tage
                    </Button>
                    <Button 
                      variant={previewDays === 90 ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setPreviewDays(90)}
                    >
                      90 Tage
                    </Button>
                  </div>
                </CardDescription>
              </CardHeader>
              
              {/* VSL Funnel Summary */}
              {vslStats && (
                <div className="px-6 pb-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{vslStats.totalViews}</div>
                      <div className="text-xs text-muted-foreground">VSL Aufrufe</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{vslStats.fromPreviews}</div>
                      <div className="text-xs text-muted-foreground">Von Previews</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{vslStats.directVisits}</div>
                      <div className="text-xs text-muted-foreground">Direkt</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{vslStats.totalVideoPlays}</div>
                      <div className="text-xs text-muted-foreground">Videos gestartet</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">{vslStats.conversionRate.toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground">Video-Rate</div>
                    </div>
                  </div>
                </div>
              )}
              
              <CardContent className="p-0">
                {previewStatsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center space-y-4">
                      <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                      <p className="text-sm text-muted-foreground">Lade Preview-Statistiken...</p>
                    </div>
                  </div>
                ) : previewStats.length === 0 ? (
                  <div className="text-center py-12">
                    <Eye className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">Keine Preview-Seiten gefunden</h3>
                    <p className="text-muted-foreground">
                      Es wurden noch keine personalisierten Vorschau-Seiten erstellt.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border" data-testid="previews-list">
                    <div className="grid grid-cols-6 gap-4 p-4 bg-muted/50 text-xs font-medium text-muted-foreground">
                      <div>Firma</div>
                      <div className="text-center">Views</div>
                      <div className="text-center">Video</div>
                      <div className="text-center">CTA</div>
                      <div>Letzter Besuch</div>
                      <div>Status</div>
                    </div>
                    {previewStats.map((stat) => (
                      <div 
                        key={stat.slug} 
                        className="grid grid-cols-6 gap-4 p-4 items-center hover-elevate cursor-pointer"
                        onClick={() => setSelectedPreviewSlug(selectedPreviewSlug === stat.slug ? null : stat.slug)}
                        data-testid={`row-preview-${stat.slug}`}
                      >
                        <div>
                          <p className="font-medium text-sm truncate">{stat.companyName}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" />
                            /preview/{stat.slug}
                          </p>
                        </div>
                        <div className="text-center">
                          <Badge variant="outline" className="tabular-nums">
                            <Eye className="h-3 w-3 mr-1" />
                            {stat.views}
                          </Badge>
                        </div>
                        <div className="text-center">
                          <Badge variant={stat.videoClicks > 0 ? "secondary" : "outline"} className="tabular-nums">
                            <PlayCircle className="h-3 w-3 mr-1" />
                            {stat.videoClicks}
                          </Badge>
                        </div>
                        <div className="text-center">
                          <Badge variant={stat.ctaClicks > 0 ? "default" : "outline"} className="tabular-nums">
                            <MousePointer className="h-3 w-3 mr-1" />
                            {stat.ctaClicks}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {stat.lastViewedAt 
                            ? new Date(stat.lastViewedAt).toLocaleDateString('de-DE', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })
                            : '-'
                          }
                        </div>
                        <div>
                          <Badge 
                            variant={stat.status === 'converted' ? 'default' : stat.status === 'active' ? 'secondary' : 'outline'}
                          >
                            {stat.status === 'converted' ? 'Konvertiert' : 
                             stat.status === 'active' ? 'Aktiv' : 
                             stat.status === 'expired' ? 'Abgelaufen' : stat.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Event Timeline for selected preview */}
                {selectedPreviewSlug && (
                  <div className="border-t p-4 bg-muted/30">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-sm">
                        Event-Timeline: {previewStats.find(s => s.slug === selectedPreviewSlug)?.companyName}
                      </h4>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedPreviewSlug(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {previewEventsLoading ? (
                      <div className="text-center py-4">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                      </div>
                    ) : previewEvents.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Keine Events in den letzten {previewDays} Tagen.
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {previewEvents.map((event) => (
                          <div key={event.id} className="flex items-center gap-3 text-sm">
                            <div className="w-20 text-xs text-muted-foreground">
                              {new Date(event.createdAt).toLocaleDateString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                            <Badge variant={
                              event.eventType === 'cta_click' ? 'default' :
                              event.eventType === 'video_click' ? 'secondary' : 'outline'
                            }>
                              {event.eventType === 'page_view' && <Eye className="h-3 w-3 mr-1" />}
                              {event.eventType === 'video_click' && <PlayCircle className="h-3 w-3 mr-1" />}
                              {event.eventType === 'cta_click' && <MousePointer className="h-3 w-3 mr-1" />}
                              {event.eventType === 'page_view' ? 'Seitenaufruf' :
                               event.eventType === 'video_click' ? 'Video geklickt' :
                               event.eventType === 'cta_click' ? 'CTA geklickt' : event.eventType}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </FloatingCard>
          </TabsContent>

          <TabsContent value="signal-room">
            <SignalRoomAdminPanel />
          </TabsContent>
        </Tabs>
      </AnimatedSection>
      
      {/* Reject Confirmation Dialog */}
      <AlertDialog open={!!rejectingUser} onOpenChange={(open) => !open && setRejectingUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer ablehnen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie {rejectingUser?.firstName} {rejectingUser?.lastName} wirklich von der Warteliste ablehnen?
              Diese Aktion kann später rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-reject">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (rejectingUser) {
                  updateUserMutation.mutate({
                    userId: rejectingUser.id,
                    updates: { status: 'rejected' }
                  })
                  setRejectingUser(null)
                }
              }}
              data-testid="button-confirm-reject"
            >
              Ablehnen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}